<template>
  <div class="jump-game-container">
    <a class="back-button" @click="goBack">⬅ {{back_label[language]}}</a>

    <!-- Game Stats (Score, Timer) -->
    <div class="game-stats">
      <div class="game-info">{{score_label[language]}}: {{ score }}</div>
      <div class="timer">{{ timeleft_label[language] }}: {{ timeLeft }}s</div>
    </div>

    <!-- AR Camera View -->
    <div class="ar-view">
      <video id="input_video" autoplay playsinline></video>
      <canvas id="output_canvas"></canvas>
    </div>

    <!-- Jump Cue -->
    <div v-if="showCue" class="jump-cue">{{ jump_now_label[language] }}</div>

    <MorningWinOverlay :message="overlayMessage[language]" :visible="isMorningWinOverlayVisible" />
    <MorningLoseOverlay :message="overlayMessage[language]" :visible="isMorningLoseOverlayVisible" />
    <NoonWinOverlay :message="overlayMessage[language]" :visible="isNoonWinOverlayVisible" />
    <NoonLoseOverlay :message="overlayMessage[language]" :visible="isNoonLoseOverlayVisible" />
    <AfternoonWinOverlay :message="overlayMessage[language]" :visible="isAfternoonWinOverlayVisible" />
    <AfternoonLoseOverlay :message="overlayMessage[language]" :visible="isAfternoonLoseOverlayVisible" />
    <EveningWinOverlay :message="overlayMessage[language]" :visible="isEveningWinOverlayVisible" />
    <EveningLoseOverlay :message="overlayMessage[language]" :visible="isEveningLoseOverlayVisible" />
    <ProfPepOverlay :message="overlayMessage[language]" :visible="isOverlayVisible" @start-game="onStartGame" />
    <!-- Badge for Success/Fail -->
    <div v-if="badgeMessage" :class="['badge', badgeType]">{{ badgeMessage }}</div>
  </div>
</template>

<script>
import { Camera } from "@mediapipe/camera_utils";
import { Pose } from "@mediapipe/pose";
import ProfPepOverlay from '@/components/ProfPepOverlay.vue';
import MorningWinOverlay from '@/components/MorningWinOverlay.vue';
import MorningLoseOverlay from '@/components/MorningLoseOverlay.vue';
import NoonWinOverlay from '@/components/NoonWinOverlay.vue';
import NoonLoseOverlay from '@/components/NoonLoseOverlay.vue';
import AfternoonWinOverlay from '@/components/AfternoonWinOverlay.vue';
import AfternoonLoseOverlay from '@/components/AfternoonLoseOverlay.vue';
import EveningWinOverlay from '@/components/EveningWinOverlay.vue';
import EveningLoseOverlay from '@/components/EveningLoseOverlay.vue';
import { Howler } from 'howler';
import { sounds } from '@/utils/sounds';

export default {
  components: {
    ProfPepOverlay,
    MorningWinOverlay,
    MorningLoseOverlay,
    NoonWinOverlay,
    NoonLoseOverlay,
    AfternoonWinOverlay,
    AfternoonLoseOverlay,
    EveningWinOverlay,
    EveningLoseOverlay,
  },
  data() {
    return {
isShuttingDown: false,
      back_label : {en:"Back", el:"Πίσω", it:"Indietro", ro:"Înapoi", uk:"Назад"},
     Round_label: {en:"Round", el:"Γύρος", it:"Round", ro:"Rundă", uk:"Раунд"},
      timeleft_label: {en:"Time left", el:"Χρόνος", it:"Tempo rimanente", ro:"Timp rămas", uk:"Час залишився"},
      score_label: {en:"Score", el:"Πόντοι", it:"Punteggio", ro:"Scor", uk:"Очки"},
      correct_label: {en:"Correct!", el:"Σωστά!", it:"Corretto", ro:"Corect!", uk:"Правильно!"},
      wrong_label: {en:"Wrong...", el:"Λάθος...", it:"Sbagliato", ro:"Greșit...", uk:"Неправильно..."},
      jump_now_label: {
        en: "Jump Now!",
        el: "Πήδα τώρα!",
        it: "Salta ora!",
        ro: "Sari acum!",
        uk: "Стрибай зараз!"
      },

      isSyncing: false,

      camera: null,
      scoreThreshold: 200,
      overlayMessage: {en:'This is the message',el:"Αυτό είναι το μήνυμα"},
      isOverlayVisible: true,
      isMorningWinOverlayVisible: false,
      isMorningLoseOverlayVisible: false,
      isNoonWinOverlayVisible: false,
      isNoonLoseOverlayVisible: false,
      isAfternoonWinOverlayVisible: false,
      isAfternoonLoseOverlayVisible: false,
      isEveningWinOverlayVisible: false,
      isEveningLoseOverlayVisible: false,
      score: 0,
      timeLeft: 60,
      timer: null,
      turnTimer: null,
      jumpDetected: false,
      showCue: false,
      cueActive: false,
      badgeMessage: '',
      badgeType: '',
      foods: [],
      currentFood: null,
      gamePhase: 'initialization',
      categories: {
        proteins: [
          { name: 'egg', src: require('../assets/game-elements/meat_fish_milk_eggs/eggs.png') },
          { name: 'steak', src: require('../assets/game-elements/meat_fish_milk_eggs/milk.png') },
        ],
        vegetables: [
          { name: {en:'carrot', el:'καρότο', it:'carota', ro:'morcov', uk:'морква'}, src: require('../assets/game-elements/vegetables/carrot.png') },
          { name: {en:'broccoli', el:'μπρόκολο', it:'broccoli', ro:'broccoli', uk:'брокколі'}, src: require('../assets/game-elements/vegetables/broccoli.png') },
        ],
      },
    };
  },
  methods: {
    async goBack() {
      sounds.whoosh.play();
      sounds.drums_for_games_extended.stop();
      this.isShuttingDown=true;
      clearInterval(this.timer);
      clearInterval(this.frameTimer);

      if (this.camera) {
        try {
          this.camera.stop();
        } catch (e) {
          console.error("Camera stop failed on unmount:", e);
        }
      }

      // Optionally nullify the camera and pose to prevent further usage
      this.camera = null;

      if (this.pose) {
        this.pose.close(); // dispose pose properly if using pose.setOptions() instance
        this.pose = null;
      }

      // End the game as if the time ran out
      await this.endGame();
    },

    startGame() {
      this.overlayMessage = {
        en: 'Get ready to jump!',
        el: 'Ετοιμάσου για άλματα!',
        it: 'Preparati a saltare!',
        ro: 'Pregătește-te să sari!',
        uk: 'Приготуйся стрибати!'
      };
      this.isOverlayVisible = true; // Show overlay at the start
      this.initializeMediapipePose();
    },
    onStartGame() {
      Howler.ctx.resume();
      sounds.drums_for_games_extended.play();
      this.isOverlayVisible = false; // Hide after delay
      this.loadFoods();
      this.timeLeft = 60;
      this.score = 0;
      this.startTimer();
      this.startTurn();

      // Play a sound
      sounds.get_ready.play();
    },

    startTimer() {
      clearInterval(this.timer);
      this.timer = setInterval(() => {
        this.timeLeft--;
        if (this.timeLeft <= 0) {
          clearInterval(this.timer);
          if (this.gamePhase !== 'ended') {
            this.endGame();
          }
        }
      }, 1000);
    },

    loadFoods() {
      const allFoods = Object.values(this.categories).flat();
      this.foods = this.shuffleArray(allFoods);
    },

    shuffleArray(array) {
      return array.sort(() => Math.random() - 0.5);
    },

    startTurn() {
      this.showCue = true;
      this.cueActive = true;
      setTimeout(() => {
        this.showCue = false;
        setTimeout(() => {
          this.cueActive = false;
          this.startTurn(); // Start next turn
        }, 3000); // 3 seconds for jump validity
      }, 1500); // Cue displayed for 1 second
    },

    initializeMediapipePose() {
      const videoElement = document.getElementById('input_video');
      const canvasElement = document.getElementById('output_canvas');
      const canvasCtx = canvasElement.getContext('2d');

      videoElement.width = window.innerWidth;
      videoElement.height = window.innerHeight;
      canvasElement.width = window.innerWidth;
      canvasElement.height = window.innerHeight;

      const pose = new Pose({
        locateFile: (file) => `https://cdn.jsdelivr.net/npm/@mediapipe/pose/${file}`,
      });

      pose.setOptions({
        modelComplexity: 0,
        smoothLandmarks: true,
        enableSegmentation: false,
        minDetectionConfidence: 0.5,
        minTrackingConfidence: 0.5,
      });

      pose.onResults((results) => {
        canvasCtx.clearRect(0, 0, canvasElement.width, canvasElement.height);
        canvasCtx.drawImage(
          results.image,
          0,
          0,
          canvasElement.width,
          canvasElement.height
        );

        if (results.poseLandmarks) {
          this.drawLandmarks(results.poseLandmarks, canvasCtx);
          if (this.gamePhase === 'initialization') {
            this.startGameplayPhase();
          }
          if (this.gamePhase === 'gameplay' && this.cueActive) {
            this.detectJump(results.poseLandmarks);
          }
        }
      });

      this.camera = new Camera(videoElement, {
        onFrame: async () => {
          if (this.isShuttingDown) return; // Don't send if shutting down
          if (videoElement.readyState >= 2) {
            try {
              await pose.send({ image: videoElement });
            } catch (e) {
              console.error("Error during pose.send:", e);
            }
          }
        },
        width: 320,
        height: 180,
      });

      this.camera.start();
    },

    drawLandmarks(landmarks, ctx) {
      landmarks.forEach((landmark) => {
        ctx.beginPath();
        ctx.arc(
          landmark.x * ctx.canvas.width,
          landmark.y * ctx.canvas.height,
          5,
          0,
          2 * Math.PI
        );
        ctx.fillStyle = 'red';
        ctx.fill();
      });
    },

    detectJump(landmarks) {
      const leftAnkle = landmarks[27];
      const rightAnkle = landmarks[28];
      const leftShoulder = landmarks[11];
      const rightShoulder = landmarks[12];
      const leftHip = landmarks[23];
      const rightHip = landmarks[24];

      if (!leftAnkle || !rightAnkle || !leftShoulder || !rightShoulder || !leftHip || !rightHip) return;

      const avgAnkleY = (leftAnkle.y + rightAnkle.y) / 2;
      const avgShoulderY = (leftShoulder.y + rightShoulder.y) / 2;
      const avgHipY = (leftHip.y + rightHip.y) / 2;

      if (
        (avgAnkleY < 0.5 || avgShoulderY < 0.4 || avgHipY < 0.6) &&
        !this.jumpDetected
      ) {
        this.jumpDetected = true;
        this.score += 10;
        // this.showBadge('success', '+10 Points!');
        this.showBadge('success', this.correct_label[this.language]);
        this.displayFood();
        // Play a sound
        sounds.got_one.play();
      } else if (avgAnkleY >= 0.5 && avgShoulderY >= 0.4 && avgHipY >= 0.6) {
        this.jumpDetected = false;
      }
    },

    displayFood() {
      if (this.foods.length > 0) {
        this.currentFood = this.foods.pop();
        setTimeout(() => {
          this.currentFood = null;
        }, 2000); // Show the food for 2 seconds
      }
    },

    showBadge(type, message) {
      this.badgeType = type;
      this.badgeMessage = message;
      setTimeout(() => {
        this.badgeMessage = '';
      }, 2000);
    },

    startGameplayPhase() {
      this.gamePhase = 'gameplay';
    },

    async updateSeedPoints(seedType, change) {
  const token = localStorage.getItem("token");
  if (!token) return;

  try {
    const response = await fetch("https://erebus2.athenarc.gr/duse/update-seed-points", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ seedType, change }),
    });

    if (response.ok) {
      console.log(`Seed points updated: ${change} to ${seedType}`);
    } else {
      console.error("Failed to update seed points.");
    }
  } catch (error) {
    console.error("Error updating seed points:", error);
  }
},
    async endGame() {
      sounds.drums_for_games_extended.stop();
      // If overlay is still visible, user exited before game started; do not update seeds or play sounds
      if (this.isOverlayVisible) {
        // User exited before game started; do not update seeds
        this.$router.push({ name: 'GameSelection' });
        return;
      }
      let seedType = "";
      let change = 0;

      if (this.score > this.scoreThreshold) {
        change = 1; // Win: Gain a seed
      } else {
        change = -1; // Lose: Lose a seed
      }
      if (this.score > this.scoreThreshold) {
        // Play a sound
        sounds.win_sound.play();
      } else {
        // Play a sound
        sounds.lose_sound.play();
      }
      if (this.daypart == 'morning') {
        seedType = "blue";

        if (this.score > this.scoreThreshold) {
          this.overlayMessage = {en:`Congratulations! You scored ${this.score} points!`, el:`Συγχαρητήρια! Η βαθμολογία σου είναι ${this.score} πόντοι!`, it:`Congratulazioni! Hai totalizzato ${this.score} punti!`, ro:`Felicitări! Ai obținut ${this.score} puncte!`, uk:`Вітаємо! Ви набрали ${this.score} балів!`};
          this.isMorningWinOverlayVisible = true; // Show overlay at the end
        } else {
          this.overlayMessage = {en:`You scored ${this.score} points. Try again…`, el:`Συγκέντρωσες ${this.score} πόντους. Προσπάθησε ξανά…`, it:`Hai totalizzato ${this.score} punti. Riprova…`, ro:`Ai obținut ${this.score} puncte. Încearcă din nou…`, uk:`Ви набрали ${this.score} балів. Спробуйте ще раз…`};
          this.isMorningLoseOverlayVisible = true; // Show overlay at the end
        }
      } else if (this.daypart == 'noon') {
        seedType = "red";

        if (this.score > this.scoreThreshold) {
          this.overlayMessage = {en:`Congratulations! You scored ${this.score} points!`, el:`Συγχαρητήρια! Η βαθμολογία σου είναι ${this.score} πόντοι!`, it:`Congratulazioni! Hai totalizzato ${this.score} punti!`, ro:`Felicitări! Ai obținut ${this.score} puncte!`, uk:`Вітаємо! Ви набрали ${this.score} балів!`};
          this.isNoonWinOverlayVisible = true; // Show overlay at the end
        } else {
          this.overlayMessage = {en:`You scored ${this.score} points. Try again…`, el:`Συγκέντρωσες ${this.score} πόντους. Προσπάθησε ξανά…`, it:`Hai totalizzato ${this.score} punti. Riprova…`, ro:`Ai obținut ${this.score} puncte. Încearcă din nou…`, uk:`Ви набрали ${this.score} балів. Спробуйте ще раз…`};
          this.isNoonLoseOverlayVisible = true; // Show overlay at the end
        }
      } else if (this.daypart == 'afternoon') {
        seedType = "green";
        if (this.score > this.scoreThreshold) {
          this.overlayMessage = {en:`Congratulations! You scored ${this.score} points!`, el:`Συγχαρητήρια! Η βαθμολογία σου είναι ${this.score} πόντοι!`, it:`Congratulazioni! Hai totalizzato ${this.score} punti!`, ro:`Felicitări! Ai obținut ${this.score} puncte!`, uk:`Вітаємо! Ви набрали ${this.score} балів!`};
          this.isAfternoonWinOverlayVisible = true; // Show overlay at the end
        } else {
          this.overlayMessage = {en:`You scored ${this.score} points. Try again…`, el:`Συγκέντρωσες ${this.score} πόντους. Προσπάθησε ξανά…`, it:`Hai totalizzato ${this.score} punti. Riprova…`, ro:`Ai obținut ${this.score} puncte. Încearcă din nou…`, uk:`Ви набрали ${this.score} балів. Спробуйте ще раз…`};
          this.isAfternoonLoseOverlayVisible = true; // Show overlay at the end
        }
      } else if (this.daypart == 'night') {
        seedType = "yellow";
        if (this.score > this.scoreThreshold) {
          this.overlayMessage = {en:`Congratulations! You scored ${this.score} points!`, el:`Συγχαρητήρια! Η βαθμολογία σου είναι ${this.score} πόντοι!`, it:`Congratulazioni! Hai totalizzato ${this.score} punti!`, ro:`Felicitări! Ai obținut ${this.score} puncte!`, uk:`Вітаємо! Ви набрали ${this.score} балів!`};
          this.isEveningWinOverlayVisible = true; // Show overlay at the end
        } else {
          this.overlayMessage = {en:`You scored ${this.score} points. Try again…`, el:`Συγκέντρωσες ${this.score} πόντους. Προσπάθησε ξανά…`, it:`Hai totalizzato ${this.score} punti. Riprova…`, ro:`Ai obținut ${this.score} puncte. Încearcă din nou…`, uk:`Ви набрали ${this.score} балів. Спробуйте ще раз…`};
          this.isEveningLoseOverlayVisible = true; // Show overlay at the end
        }
      }
      // Send seed update to the backend
      await this.updateSeedPoints(seedType, change);

      setTimeout(() => {
        this.$router.push({ name: 'GameSelection' }); // Redirect after delay
      }, 3000); // 3 seconds delay
    },

    async storeInIndexedDB(gameState) {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open("GameDB", 1);
    request.onupgradeneeded = function (event) {
      const db = event.target.result;
      if (!db.objectStoreNames.contains("gameStates")) {
        db.createObjectStore("gameStates", { keyPath: "id", autoIncrement: true });
      }
    };

    request.onsuccess = function (event) {
      const db = event.target.result;
      const transaction = db.transaction("gameStates", "readwrite");
      const store = transaction.objectStore("gameStates");

      // Check for existing game state before saving
      const getAllRequest = store.getAll();
      getAllRequest.onsuccess = function () {
        const existingRecords = getAllRequest.result;
        const duplicate = existingRecords.find(
          (record) => record.gameName === gameState.gameName && record.datePlayed === gameState.datePlayed
        );

        if (!duplicate) {
          store.add(gameState);
        } else {
          console.warn("Duplicate game state detected. Skipping save.");
        }

        resolve();
      };

      getAllRequest.onerror = function (event) {
        reject("Error checking existing records:", event.target.error);
      };
    };

    request.onerror = function (event) {
      reject("Error opening IndexedDB:", event.target.error);
    };
  });
}
,

async getUnsyncedGameStates() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open("GameDB", 1);

    request.onsuccess = function (event) {
      const db = event.target.result;
      const transaction = db.transaction("gameStates", "readonly");
      const store = transaction.objectStore("gameStates");

      const getAllRequest = store.getAll();
      getAllRequest.onsuccess = function () {
        resolve(getAllRequest.result);
      };
      getAllRequest.onerror = function (event) {
        reject("Error retrieving game states:", event.target.error);
      };
    };
  });
},

async deleteSyncedGameStates(syncedGames) {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open("GameDB", 1);

    request.onsuccess = function (event) {
      const db = event.target.result;
      const transaction = db.transaction("gameStates", "readwrite");
      const store = transaction.objectStore("gameStates");

      syncedGames.forEach((game) => {
        store.delete(game.id); // Delete only specific records
      });

      resolve();
    };

    request.onerror = function (event) {
      reject("Error clearing IndexedDB:", event.target.error);
    };
  });
}
,
async syncWithMongo() {
  if (this.isSyncing) return; // Prevent duplicate sync
  this.isSyncing = true;

  const token = localStorage.getItem("token");
  if (!token) {
    console.error("User not authenticated. Cannot sync.");
    this.isSyncing = false;
    return;
  }

  try {
    const unsyncedGames = await this.getUnsyncedGameStates();
    if (unsyncedGames.length === 0) {
      console.log("No game states to sync.");
      this.isSyncing = false;
      return;
    }

    const response = await fetch("https://erebus2.athenarc.gr/duse/save-game-state", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ gameStates: unsyncedGames }),
    });

    if (response.ok) {
      console.log("Game states synced successfully.");
      await this.deleteSyncedGameStates(unsyncedGames);
    } else {
      console.error("Failed to sync game states.");
    }
  } catch (error) {
    console.error("Error syncing with MongoDB:", error);
  } finally {
    this.isSyncing = false; // Ensure flag is reset
  }
},
    async saveGameState() {
  const token = localStorage.getItem("token");
  const username = localStorage.getItem("username");

  if (!username) {
    console.error("User not logged in. Cannot save game state.");
    return;
  }

  const gameState = {
    username,
    gameName: this.$route.name,
    score: this.score,
    datePlayed: new Date().toISOString(),
  };

  try {
    // Store in IndexedDB regardless of network status
    await this.storeInIndexedDB(gameState);

    if (navigator.onLine && token) {
      await this.syncWithMongo();
    } else {
      console.warn("No internet connection. Game state saved locally.");
    }
  } catch (error) {
    console.error("Error saving game state:", error);
  }
},

  },
  created()
  {
    this.language = localStorage.getItem("language") || "en"; // Default to English
    console.log("LANGUAGE "+this.language);
  },
  mounted() {
  console.log("Current route path:", this.$route.path);

  const daypart = this.$route.path.split("/")[2] || "daypart";
  this.daypart = daypart;
  this.startGame();

  // Check for internet connectivity
  window.addEventListener("online", async () => {
    console.log("Back online! Syncing game states...");
    await this.syncWithMongo();
  });
},
  beforeUnmount() {
    clearInterval(this.timer);
    if (this.camera) {
      try {
        this.camera.stop();
      } catch (e) {
        console.error("Camera stop failed on unmount:", e);
      }
    }

    // Optionally nullify the camera and pose to prevent further usage
    this.camera = null;

    if (this.pose) {
      this.pose.close(); // dispose pose properly if using pose.setOptions() instance
      this.pose = null;
    }
    this.saveGameState();
  },
  beforeRouteLeave(to, from, next) {
    // Save game state when the user navigates away
    this.saveGameState();
    next();
  },


};
</script>

<style scoped>
.jump-game-container {
  position: relative;
  width: 100vw;
  height: 100vh;
  background-size: cover;
}

.ar-view {
  position: relative;
  width: 100%;
  height: 100%;
}

video,
canvas {
  position: absolute;
  top: 0;
  left: 0;
  width: auto;
  /* Maintain aspect ratio */
  height: 100%;
  /* Full height of the screen */
  object-fit: cover;
  /* Avoid distortion */
  transform: scaleX(-1);
  /* Mirror for selfie mode */
}

.food-target {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.food-image {
  width: 100px;
  height: 100px;
}

.badge {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 24px;
  color: white;
  padding: 10px 20px;
  border-radius: 10px;
  text-align: center;
}

.badge.success {
  background-color: green;
}

.badge.fail {
  background-color: red;
}

.jump-cue {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 32px;
  color: yellow;
  background: rgba(0, 0, 0, 0.7);
  padding: 10px 20px;
  border-radius: 10px;
  text-align: center;
  z-index: 1000;
}

.game-stats {
  position: absolute;
  top: 70px;
  /* Fixed position to prevent being pushed */
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
  /* Higher than video and canvas */
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 90%;
  margin: 0 auto;
  padding: 10px;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 8px;
  color: white;
  font-size: 20px;
}

.back-button {
  z-index: 1001;
}
</style>